/* eslint-disable padding-line-between-statements */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ThemeContext } from './context';
import PropTypes from 'prop-types';
import { DEFAULT_THEME_ID, ThemeDomEnum } from '../../enums';
import { resolveTheme } from '../../resolve-theme';

const propTypes = {
	rootId: PropTypes.string,
	defaultThemeId: PropTypes.string,
	controlledThemeId: PropTypes.string,
	children: PropTypes.node.isRequired,
};
const defaultProps = {
	rootId: ThemeDomEnum.ROOT_ID,
	defaultThemeId: DEFAULT_THEME_ID,
};

function getThemeIdFromRoot(rootId) {
	if (typeof document === 'undefined') {
		return null;
	}

	const el = document.getElementById(rootId);

	return el?.dataset?.theme || null;
}

function ThemeProvider({
	rootId,
	defaultThemeId,
	controlledThemeId,
	children,
}) {
	const [themeIdState, setThemeIdState] = useState(() => (
		getThemeIdFromRoot(rootId) || defaultThemeId
	));

	// Controlled：由外部（如管理端預覽）直接指定 themeId，不讀 DOM 也不掛 root-background observer，
	const isControlled = typeof controlledThemeId === 'string';
	const themeId = isControlled ? controlledThemeId : themeIdState;

	// 給 Phase2 用：程式內切 theme，同步寫回 DOM dataset（維持單一來源）
	const setThemeId = useCallback(nextThemeId => {
		if (isControlled) {
			return;
		}

		setThemeIdState(nextThemeId);

		const el = document.getElementById(rootId);

		if (el) {
			el.dataset.theme = nextThemeId;
		}
	}, [rootId, isControlled]);

	// 監聽 root-background 的 data-theme 變化（例如 settingBackground/setRootTheme）
	useEffect(() => {
		if (isControlled) {
			return;
		}

		const el = document.getElementById(rootId);

		if (!el || typeof MutationObserver === 'undefined') {
			return;
		}

		const sync = () => {
			const next = el.dataset.theme || defaultThemeId;

			setThemeIdState(prev => (prev === next ? prev : next));
		};

		sync();

		const observer = new MutationObserver(sync);

		observer.observe(el, { attributes: true, attributeFilter: ['data-theme'] });

		return () => observer.disconnect();
	}, [rootId, defaultThemeId, isControlled]);

	const theme = useMemo(() => resolveTheme(themeId), [themeId]);
	const value = useMemo(() => ({
		themeId,
		theme,
		assets: theme.assets,
		setThemeId,
	}), [themeId, theme, setThemeId]);

	return (
		<ThemeContext.Provider value={value}>
			{children}
		</ThemeContext.Provider>
	);
}

ThemeProvider.propTypes = propTypes;
ThemeProvider.defaultProps = defaultProps;

export default ThemeProvider;
